import { getBioRepGroupKey, getSimpleBioNumber } from '@resistapp/common/sampling-points';
import { Dictionary, get, keyBy, mapValues, mean, uniq, values } from 'lodash';
import { GeneGrouping, GetGroup, sixteenS } from './assays';
import { isTrace } from './flag-utils';
import { Abundance, AbundanceByBA, AbundancesByUBA, FullAbundance, FullSample } from './types';

export function buildSortIdxByGene(
  allSamples: FullSample[],
  grouping: GeneGrouping,
  getGroup: GetGroup,
  focusedSamples?: FullSample[],
) {
  const abundanceBySBA = deprecatedGetAbundancesBySBA(allSamples);
  const allAssays = getUniqueAssays(allSamples).filter(assay => getGroup(assay) !== sixteenS || grouping === sixteenS);
  const meanByGroup = calcMeanByGroup(abundanceBySBA, grouping, false, getGroup);
  const meanByGene = calcMeanByAssay(abundanceBySBA, false);
  const flagsByGroup = calcMeanByGroup(abundanceBySBA, grouping, true, getGroup);
  const flagsByGene = calcMeanByAssay(abundanceBySBA, true);

  // TODO support duplicate genes ?
  const assayToGene: Dictionary<string> = {};
  allSamples.forEach(sample => {
    sample.abundances.forEach(abundance => {
      assayToGene[abundance.assay] = abundance.gene;
    });
  });

  allAssays.sort((assayA, assayB) => {
    // The gene fallbacks are for 16s
    const groupA = getGroup(assayA, grouping) as string;
    const groupB = getGroup(assayB, grouping) as string;

    const groupPart = meanByGroup[groupB] - meanByGroup[groupA];
    const groupFlag = flagsByGroup[groupB] - flagsByGroup[groupA];
    const groupAlphaPart = groupA > groupB ? -1 : groupB > groupA ? 1 : 0;
    const genePart = meanByGene[assayB] - meanByGene[assayA];
    const geneFlag = flagsByGene[assayB] - flagsByGene[assayA];
    const assayAlphaPart = assayA < assayB ? -0.01 : assayB < assayA ? 0.01 : 0;
    return (groupPart || groupFlag || groupAlphaPart) * 1000000000 + (genePart || geneFlag || assayAlphaPart);
  });

  let idx = 0;
  const focusedAssays = focusedSamples && getUniqueAssays(focusedSamples);
  const assayIsFocused = focusedAssays && keyBy(focusedAssays, a => a);
  return allAssays.reduce<Dictionary<number>>((acc, assay) => {
    if (!assayIsFocused || assayIsFocused[assay]) {
      const gene = assayToGene[assay];
      if (gene) {
        acc[gene] = idx++;
      }
    }
    return acc;
  }, {});
}

function deprecatedGetAbundancesBySBA(samples: FullSample[]): AbundancesByUBA {
  // NOTE!
  // - In normal projects, there were 50 old samples that had duplicate non-bioreplicates (10A and 11A) with the same time and linking to the same smplPnt
  //   - See eg 10A and 11A in https://docs.google.com/spreadsheets/d/1xsuxhnd8DyXO8cAY9CMmIaFDLG16W9-9-m0w_pv9RyU/edit?gid=707127087#gid=707127087
  // - In addition, pooled projects have tons of these duplicates.
  // - This function is only needed for determening the heatmap anitb. and gene sort order, and there it is ok to just use the first one
  return samples.reduce<AbundancesByUBA>((acc, sample) => {
    const bioGroupKey = getBioRepGroupKey(sample);
    const byBA = acc[bioGroupKey] || {};
    if (!byBA[sample.bioRep]) {
      byBA[sample.bioRep] = sample.abundances.reduce<Dictionary<FullAbundance>>((acc2, r) => {
        if (get(acc2, r.assay, undefined)) {
          throw Error(`Duplicate abundance for sample ${getSimpleBioNumber(sample)} ${r.assay}`);
        }
        acc2[r.assay] = r;
        return acc2;
      }, {});
      acc[bioGroupKey] = byBA;
    }
    return acc;
  }, {});
}

function getUniqueAssays(samples: FullSample[]) {
  const allAssays = samples.reduce<string[]>((acc, sample) => {
    sample.abundances.forEach(r => {
      // Include both measured and unmeasured (estimated-only) assays so that every gene present in any mode
      // participates in the global sort order.
      acc.push(r.assay);
    });
    return acc;
  }, []);

  const assays = uniq(allAssays);
  if (allAssays.length !== assays.length * samples.length) {
    throw Error('All assays not analysed for all samples');
  }
  return assays;
}

function calcMeanByGroup(
  abundanceByUBG: AbundancesByUBA,
  grouping: GeneGrouping,
  useFlag: boolean,
  getGroup: GetGroup,
) {
  const valuesByGroup: Dictionary<number[]> = buildValuesBy(
    abundanceByUBG,
    assay => getGroup(assay, grouping),
    useFlag,
  );
  return mapValues(valuesByGroup, mean);
}

function calcMeanByAssay(abundanceBySBG: AbundancesByUBA, useFlag: boolean) {
  const valuesByAssay: Dictionary<number[]> = buildValuesBy(abundanceBySBG, assay => assay, useFlag);
  return mapValues(valuesByAssay, mean);
}

function buildValuesBy(abundanceByUBA: AbundancesByUBA, by: (assay: string) => string | undefined, useFlag = false) {
  const valuesBy: Dictionary<number[]> = {};
  values(abundanceByUBA).forEach((abundanceByBA: AbundanceByBA | undefined) => {
    if (!abundanceByBA) {
      throw Error('Unexpected undefined abundanceByBG');
    }
    values(abundanceByBA).forEach((abundanceByG?: Dictionary<Abundance>) => {
      if (!abundanceByG) {
        throw Error('Unexpected undefined abundanceByG');
      }
      values(abundanceByG).forEach(r => {
        const b = by(r.assay) as string;
        if (!get(valuesBy, b, undefined)) {
          valuesBy[b] = [];
        }
        valuesBy[b].push(useFlag ? (isTrace(r.flag) ? 1 : 0) : r.relative || 0);
      });
    });
  });
  return valuesBy;
}
